import React, { useState } from 'react';
import { Button } from '../components/Button';
import { TrustBadges } from '../components/TrustBadges';
import { CheckIcon, XIcon, StarIcon, QuoteIcon, PhoneForwardedIcon, MessageCircleIcon, CalendarCheckIcon, ZapIcon, WorkflowIcon, DatabaseIcon, BrainCircuitIcon, PhoneIcon, BellIcon, ClipboardCheckIcon, SendIcon, ChevronDownIcon } from 'lucide-react';
function ProfessionalHero() {
  return <section className="w-full bg-[#0F0F0F] py-20 md:py-32" data-id="element-2502">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-2503">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2504">
          <div data-id="element-2505">
            <div className="inline-block bg-[#F1B530] text-[#0F0F0F] px-4 py-2 rounded-full font-['Inter'] font-bold text-sm mb-6" data-id="element-2506">
              TRUSTED BY 1000+ BUSINESSES
            </div>

            <h1 className="font-['Poppins'] font-bold text-5xl md:text-6xl lg:text-7xl text-[#F5F5F5] mb-6 leading-tight" data-id="element-2507">
              Professional AI Call Answering Service
            </h1>

            <p className="font-['Inter'] text-xl text-[#A0A0A0] mb-8 leading-relaxed font-light" data-id="element-2508">
              Never miss an important call again. Our AI receptionist answers
              24/7, captures every detail, and integrates seamlessly with your
              business operations.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8" data-id="element-2509">
              <Button size="lg" data-id="element-2510">Get Started Free</Button>
              <Button variant="secondary" size="lg" theme="dark" data-id="element-2511">
                Request Demo
              </Button>
            </div>

            <div className="flex items-center gap-6 text-sm text-[#A0A0A0] font-['Inter']" data-id="element-2512">
              <div className="flex items-center gap-2" data-id="element-2513">
                <CheckIcon className="w-5 h-5 text-[#F1B530]" data-id="element-2514" />
                <span data-id="element-2515">No credit card required</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2516">
                <CheckIcon className="w-5 h-5 text-[#F1B530]" data-id="element-2517" />
                <span data-id="element-2518">7-day free trial</span>
              </div>
            </div>
          </div>

          <div className="relative" data-id="element-2519">
            <div className="bg-[#252525] border border-[#2A2A2A] rounded-3xl p-8 shadow-2xl" data-id="element-2520">
              <img src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&q=80" alt="Professional service" className="w-full h-96 object-cover rounded-2xl" data-id="element-2521" />
            </div>

            {/* Floating Stats */}
            <div className="absolute -bottom-6 -left-6 bg-[#252525]/90 backdrop-blur-md border border-[#F1B530]/30 rounded-2xl p-6 shadow-xl" data-id="element-2522">
              <div className="font-['Poppins'] text-4xl font-bold text-[#F1B530] mb-1" data-id="element-2523">
                99.9%
              </div>
              <div className="font-['Inter'] text-sm text-[#A0A0A0]" data-id="element-2524">
                Call Answer Rate
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ServiceBreakdown() {
  const services = [{
    title: 'Call Answering',
    description: 'Professional greeting and message capture for every caller',
    features: ['Custom greetings', 'Message transcription', 'Real-time notifications']
  }, {
    title: 'Appointment Booking',
    description: 'Automated scheduling integrated with your calendar',
    features: ['Calendar sync', 'Booking confirmations', 'Reminder notifications']
  }, {
    title: 'Lead Qualification',
    description: 'Intelligent screening and routing of potential customers',
    features: ['Custom questions', 'Lead scoring', 'CRM integration']
  }, {
    title: 'After Hours Support',
    description: '24/7 coverage when your team is unavailable',
    features: ['Always available', 'Urgent call routing', 'Emergency protocols']
  }];
  return <section className="w-full bg-[#1A1A1A] py-28" data-id="element-2525">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-2526">
        <div className="text-center mb-16" data-id="element-2527">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-2528">
            Complete Call Management
          </h2>
          <p className="font-['Inter'] text-xl text-[#A0A0A0] font-light max-w-3xl mx-auto" data-id="element-2529">
            Everything you need to handle calls professionally and efficiently
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2530">
          {services.map((service, index) => <div key={index} className="bg-[#252525] border border-[#2A2A2A] rounded-2xl p-8 hover:border-[#F1B530]/30 transition-all duration-300" data-id="element-2531">
              <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-4" data-id="element-2532">
                {service.title}
              </h3>
              <p className="font-['Inter'] text-[#A0A0A0] mb-6 leading-relaxed font-light" data-id="element-2533">
                {service.description}
              </p>
              <ul className="space-y-3" data-id="element-2534">
                {service.features.map((feature, idx) => <li key={idx} className="flex items-center gap-3" data-id="element-2535">
                    <CheckIcon className="w-5 h-5 text-[#F1B530] flex-shrink-0" data-id="element-2536" />
                    <span className="font-['Inter'] text-[#F5F5F5] text-sm" data-id="element-2537">
                      {feature}
                    </span>
                  </li>)}
              </ul>
            </div>)}
        </div>
      </div>
    </section>;
}
function ComparisonTable() {
  const features = ['Instant call pickup', 'Custom greeting', 'Message capture', 'Call recordings', 'Real-time notifications', 'Appointment booking', 'CRM integration', 'After hours coverage', 'Multi-language support'];
  return <section className="w-full bg-[#0F0F0F] py-28" data-id="element-2538">
      <div className="max-w-5xl mx-auto px-8 md:px-12" data-id="element-2539">
        <div className="text-center mb-16" data-id="element-2540">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-2541">
            Why Choose AI Over Traditional Services?
          </h2>
        </div>

        <div className="bg-[#252525] border border-[#2A2A2A] rounded-3xl overflow-hidden" data-id="element-2542">
          <div className="grid grid-cols-3 gap-px bg-[#2A2A2A]" data-id="element-2543">
            <div className="bg-[#252525] p-6" data-id="element-2544">
              <div className="font-['Inter'] font-semibold text-[#A0A0A0] text-sm" data-id="element-2545">
                FEATURE
              </div>
            </div>
            <div className="bg-[#252525] p-6 text-center" data-id="element-2546">
              <div className="font-['Inter'] font-bold text-[#F1B530] text-lg" data-id="element-2547">
                AI Service
              </div>
            </div>
            <div className="bg-[#252525] p-6 text-center" data-id="element-2548">
              <div className="font-['Inter'] font-semibold text-[#A0A0A0] text-sm" data-id="element-2549">
                Traditional
              </div>
            </div>
          </div>

          {features.map((feature, index) => <div key={index} className="grid grid-cols-3 gap-px bg-[#2A2A2A]" data-id="element-2550">
              <div className="bg-[#252525] p-6" data-id="element-2551">
                <div className="font-['Inter'] text-[#F5F5F5]" data-id="element-2552">{feature}</div>
              </div>
              <div className="bg-[#252525] p-6 flex justify-center" data-id="element-2553">
                <CheckIcon className="w-6 h-6 text-[#F1B530]" data-id="element-2554" />
              </div>
              <div className="bg-[#252525] p-6 flex justify-center" data-id="element-2555">
                {index < 6 ? <CheckIcon className="w-6 h-6 text-[#A0A0A0]" data-id="element-2556" /> : <XIcon className="w-6 h-6 text-[#2A2A2A]" data-id="element-2557" />}
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}
function OfficeHQPricing() {
  const plans = [{
    name: 'Starter',
    price: 'AUD $99',
    period: '/month',
    description: 'Perfect for small businesses',
    features: ['50 calls per month', 'Basic call answering', 'Message capture', 'Email notifications', 'Standard support'],
    cta: 'Start Free Trial'
  }, {
    name: 'Professional',
    price: 'AUD $249',
    period: '/month',
    description: 'Most popular for growing teams',
    features: ['200 calls per month', 'Advanced call routing', 'Appointment booking', 'CRM integration', 'Priority support', 'Call analytics'],
    cta: 'Start Free Trial',
    isPopular: true
  }, {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    description: 'For large organizations',
    features: ['Unlimited calls', 'Dedicated account manager', 'Custom integrations', 'Advanced analytics', '24/7 premium support', 'SLA guarantee'],
    cta: 'Contact Sales'
  }];
  return <section className="w-full bg-[#1A1A1A] py-28" data-id="element-2558">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-2559">
        <div className="text-center mb-16" data-id="element-2560">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-2561">
            Simple, Transparent Pricing
          </h2>
          <p className="font-['Inter'] text-xl text-[#A0A0A0] font-light" data-id="element-2562">
            Choose the plan that fits your business needs
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2563">
          {plans.map((plan, index) => <div key={index} className={`bg-[#252525] rounded-2xl p-8 border transition-all duration-300 hover:-translate-y-1 relative ${plan.isPopular ? 'border-[#F1B530] ring-2 ring-[#F1B530]/20' : 'border-[#2A2A2A] hover:border-[#F1B530]/30'}`} data-id="element-2564">
              {plan.isPopular && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#F1B530] text-[#0F0F0F] px-4 py-1 rounded-full font-['Inter'] font-semibold text-sm" data-id="element-2565">
                  Most Popular
                </div>}

              <div className="mb-6" data-id="element-2566">
                <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-2" data-id="element-2567">
                  {plan.name}
                </h3>
                <p className="font-['Inter'] text-sm text-[#A0A0A0] font-light" data-id="element-2568">
                  {plan.description}
                </p>
              </div>

              <div className="mb-8" data-id="element-2569">
                <span className="font-['Poppins'] font-bold text-5xl text-[#F5F5F5]" data-id="element-2570">
                  {plan.price}
                </span>
                <span className="font-['Inter'] text-[#A0A0A0] ml-1" data-id="element-2571">
                  {plan.period}
                </span>
              </div>

              <ul className="space-y-4 mb-8" data-id="element-2572">
                {plan.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-2573">
                    <CheckIcon className="w-5 h-5 text-[#F1B530] flex-shrink-0 mt-0.5" data-id="element-2574" />
                    <span className="font-['Inter'] text-[#F5F5F5] text-sm font-light" data-id="element-2575">
                      {feature}
                    </span>
                  </li>)}
              </ul>

              <Button className="w-full" size="lg" data-id="element-2576">
                {plan.cta}
              </Button>
            </div>)}
        </div>
      </div>
    </section>;
}
function OfficeHQTestimonials() {
  const testimonials = [{
    quote: 'The AI answering service has been a game-changer for our law firm. We never miss a potential client call, even after hours.',
    author: 'Michael Chen',
    role: 'Managing Partner, Chen & Associates',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&q=80',
    rating: 5
  }, {
    quote: 'Professional, reliable, and cost-effective. Our clients love the immediate response, and we love the detailed call summaries.',
    author: 'Sarah Johnson',
    role: 'Operations Director, MedCare Clinic',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80',
    rating: 5
  }, {
    quote: 'Setup was incredibly easy, and the integration with our CRM was seamless. Best investment we made this year.',
    author: 'David Martinez',
    role: 'CEO, RealtyPro Group',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80',
    rating: 5
  }];
  return <section className="w-full bg-[#0F0F0F] py-28" data-id="element-2577">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-2578">
        <div className="text-center mb-16" data-id="element-2579">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-2580">
            Trusted by Professionals
          </h2>
          <p className="font-['Inter'] text-xl text-[#A0A0A0] font-light" data-id="element-2581">
            See what our clients have to say
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2582">
          {testimonials.map((testimonial, index) => <div key={index} className="bg-[#252525] border border-[#2A2A2A] rounded-2xl p-8 hover:border-[#F1B530]/30 transition-all duration-300" data-id="element-2583">
              <div className="flex gap-1 mb-6" data-id="element-2584">
                {[...Array(testimonial.rating)].map((_, i) => <StarIcon key={i} className="w-5 h-5 text-[#F1B530] fill-current" data-id="element-2585" />)}
              </div>

              <QuoteIcon className="w-10 h-10 text-[#F1B530]/20 mb-4" data-id="element-2586" />

              <p className="font-['Inter'] text-[#F5F5F5] mb-8 leading-relaxed font-light" data-id="element-2587">
                "{testimonial.quote}"
              </p>

              <div className="flex items-center gap-4" data-id="element-2588">
                <img src={testimonial.image} alt={testimonial.author} className="w-12 h-12 rounded-full object-cover border-2 border-[#F1B530]/20" data-id="element-2589" />
                <div data-id="element-2590">
                  <div className="font-['Inter'] font-semibold text-[#F5F5F5]" data-id="element-2591">
                    {testimonial.author}
                  </div>
                  <div className="font-['Inter'] text-sm text-[#A0A0A0]" data-id="element-2592">
                    {testimonial.role}
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}
function OfficeHQAddOns() {
  const addOns = [{
    icon: PhoneForwardedIcon,
    name: 'Live Transfer',
    price: 'AUD $99',
    setup: 'one-time setup',
    features: ['Instant call escalation', 'Full caller context transfer', 'Smart routing rules']
  }, {
    icon: MessageCircleIcon,
    name: 'FAQ Handling',
    price: 'AUD $299',
    setup: 'one-time fee',
    features: ['Custom knowledge base', 'Brand-safe responses', 'Reduced call volume']
  }, {
    icon: CalendarCheckIcon,
    name: 'Appointment Booking',
    price: 'AUD $399',
    setup: 'one-time setup',
    features: ['Calendar sync', 'Automated confirmations', 'Reminder notifications']
  }];
  return <section className="w-full bg-[#1A1A1A] py-28" data-id="element-2593">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-2594">
        <div className="text-center mb-16" data-id="element-2595">
          <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#F5F5F5] mb-6" data-id="element-2596">
            Enhance Your Service
          </h2>
          <p className="font-['Inter'] text-lg text-[#A0A0A0] max-w-3xl mx-auto font-light" data-id="element-2597">
            Add powerful features to customize your AI answering service
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2598">
          {addOns.map((addOn, index) => {
          const Icon = addOn.icon;
          return <div key={index} className="bg-[#252525] rounded-2xl border border-[#2A2A2A] hover:border-[#F1B530]/50 transition-all duration-300 hover:-translate-y-1 p-8" data-id="element-2599">
                <div className="w-14 h-14 bg-[#F1B530] rounded-xl flex items-center justify-center mb-6" data-id="element-2600">
                  <Icon className="w-7 h-7 text-[#0F0F0F]" data-id="element-2601" />
                </div>

                <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-3" data-id="element-2602">
                  {addOn.name}
                </h3>

                <div className="mb-6" data-id="element-2603">
                  <span className="font-['Poppins'] font-semibold text-2xl text-[#F1B530]" data-id="element-2604">
                    {addOn.price}
                  </span>
                  <span className="text-[#A0A0A0] font-['Inter'] text-sm ml-2" data-id="element-2605">
                    {addOn.setup}
                  </span>
                </div>

                <ul className="space-y-3" data-id="element-2606">
                  {addOn.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-2607">
                      <CheckIcon className="w-5 h-5 text-[#F1B530] flex-shrink-0 mt-0.5" data-id="element-2608" />
                      <span className="font-['Inter'] text-[#F5F5F5] text-sm font-light" data-id="element-2609">
                        {feature}
                      </span>
                    </li>)}
                </ul>
              </div>;
        })}
        </div>
      </div>
    </section>;
}
function OfficeHQAdvancedAutomation() {
  const capabilities = [{
    icon: WorkflowIcon,
    text: 'Intelligent lead routing'
  }, {
    icon: ZapIcon,
    text: 'Automated workflows'
  }, {
    icon: DatabaseIcon,
    text: 'Direct CRM sync'
  }, {
    icon: BrainCircuitIcon,
    text: 'Data collection'
  }];
  return <section className="w-full bg-[#0F0F0F] py-28" data-id="element-2610">
      <div className="max-w-6xl mx-auto px-8 md:px-12 text-center" data-id="element-2611">
        <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl lg:text-6xl mb-6 text-[#F5F5F5]" data-id="element-2612">
          Enterprise Automation
        </h2>
        <p className="font-['Inter'] font-light text-xl md:text-2xl text-[#A0A0A0] mb-10" data-id="element-2613">
          Advanced capabilities for high-volume operations
        </p>

        <p className="font-['Inter'] text-lg text-[#A0A0A0] max-w-4xl mx-auto mb-16 leading-relaxed font-light" data-id="element-2614">
          For businesses handling 500+ calls per month, unlock enterprise-grade
          features that transform your communication infrastructure.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-2615">
          {capabilities.map((capability, index) => {
          const Icon = capability.icon;
          return <div key={index} className="bg-[#252525] rounded-2xl p-8 border border-[#2A2A2A] hover:border-[#F1B530]/30 transition-all duration-300 h-full flex flex-col items-center justify-center min-h-[180px]" data-id="element-2616">
                <div className="w-12 h-12 rounded-xl bg-[#F1B530]/10 flex items-center justify-center mb-5" data-id="element-2617">
                  <Icon className="w-6 h-6 text-[#F1B530]" data-id="element-2618" />
                </div>
                <p className="font-['Inter'] text-[#F5F5F5] font-light text-center" data-id="element-2619">
                  {capability.text}
                </p>
              </div>;
        })}
        </div>

        <Button size="lg" data-id="element-2620">Contact Sales</Button>
      </div>
    </section>;
}
function HowItWorks() {
  const steps = [{
    number: '01',
    icon: PhoneIcon,
    title: 'Call Received',
    description: 'Your AI receptionist answers instantly with your custom greeting, creating a professional first impression.'
  }, {
    number: '02',
    icon: ClipboardCheckIcon,
    title: 'Information Captured',
    description: 'Caller details, message, and context are accurately recorded and transcribed in real-time.'
  }, {
    number: '03',
    icon: BellIcon,
    title: 'Instant Notification',
    description: 'You receive immediate alerts via SMS or email with complete call summary and next steps.'
  }, {
    number: '04',
    icon: SendIcon,
    title: 'Seamless Integration',
    description: 'Call data automatically syncs to your CRM, calendar, or workflow tools for immediate action.'
  }];
  return <section className="w-full bg-[#0F0F0F] py-28" data-id="element-2621">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-2622">
        <div className="text-center mb-16" data-id="element-2623">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-2624">
            How It Works
          </h2>
          <p className="font-['Inter'] text-xl text-[#A0A0A0] font-light max-w-3xl mx-auto" data-id="element-2625">
            From call to action in four simple steps
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-2626">
          {steps.map((step, index) => {
          const Icon = step.icon;
          return <div key={index} className="relative" data-id="element-2627">
                {/* Connector line */}
                {index < steps.length - 1 && <div className="hidden lg:block absolute top-16 left-[60%] w-[80%] h-px bg-gradient-to-r from-[#F1B530] to-transparent" data-id="element-2628"></div>}

                <div className="bg-[#252525] border border-[#2A2A2A] rounded-2xl p-8 hover:border-[#F1B530]/30 transition-all duration-300 h-full" data-id="element-2629">
                  <div className="font-['Poppins'] text-5xl font-bold text-[#F1B530]/20 mb-4" data-id="element-2630">
                    {step.number}
                  </div>

                  <div className="w-14 h-14 bg-[#F1B530]/10 rounded-xl flex items-center justify-center mb-6 border border-[#F1B530]/20" data-id="element-2631">
                    <Icon className="w-7 h-7 text-[#F1B530]" data-id="element-2632" />
                  </div>

                  <h3 className="font-['Poppins'] font-bold text-xl text-[#F5F5F5] mb-4" data-id="element-2633">
                    {step.title}
                  </h3>

                  <p className="font-['Inter'] text-[#A0A0A0] leading-relaxed font-light text-sm" data-id="element-2634">
                    {step.description}
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}
function IndustrySpecificSolutions() {
  const industries = [{
    name: 'Legal Services',
    description: 'HIPAA-compliant call handling for law firms',
    features: ['Client intake screening', 'Confidential message handling', 'Urgent matter escalation', 'Case management integration']
  }, {
    name: 'Healthcare',
    description: 'Secure patient communication solutions',
    features: ['Appointment scheduling', 'Patient information capture', 'Emergency call routing', 'HIPAA compliance']
  }, {
    name: 'Real Estate',
    description: 'Never miss a property inquiry',
    features: ['Property inquiry handling', 'Viewing appointment booking', 'Lead qualification', 'Tenant support']
  }, {
    name: 'Professional Services',
    description: 'Maintain professional image 24/7',
    features: ['Client screening', 'Consultation booking', 'Message prioritization', 'Multi-office support']
  }, {
    name: 'Home Services',
    description: 'Capture every service request',
    features: ['Job booking', 'Emergency call handling', 'Quote request capture', 'Service scheduling']
  }, {
    name: 'Financial Services',
    description: 'Secure and compliant communication',
    features: ['Client verification', 'Appointment scheduling', 'Compliance adherence', 'Secure messaging']
  }];
  return <section className="w-full bg-[#1A1A1A] py-28" data-id="element-2635">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-2636">
        <div className="text-center mb-16" data-id="element-2637">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-2638">
            Industry-Specific Solutions
          </h2>
          <p className="font-['Inter'] text-xl text-[#A0A0A0] font-light max-w-3xl mx-auto" data-id="element-2639">
            Tailored AI answering services for your industry's unique needs
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2640">
          {industries.map((industry, index) => <div key={index} className="bg-[#252525] border border-[#2A2A2A] rounded-2xl p-8 hover:border-[#F1B530]/30 transition-all duration-300" data-id="element-2641">
              <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-3" data-id="element-2642">
                {industry.name}
              </h3>
              <p className="font-['Inter'] text-[#A0A0A0] mb-6 text-sm font-light" data-id="element-2643">
                {industry.description}
              </p>
              <ul className="space-y-3" data-id="element-2644">
                {industry.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-2645">
                    <CheckIcon className="w-5 h-5 text-[#F1B530] flex-shrink-0 mt-0.5" data-id="element-2646" />
                    <span className="font-['Inter'] text-[#F5F5F5] text-sm font-light" data-id="element-2647">
                      {feature}
                    </span>
                  </li>)}
              </ul>
            </div>)}
        </div>
      </div>
    </section>;
}
function OfficeHQFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: 'How does the AI answering service work?',
    answer: 'Our AI receptionist answers calls 24/7 using your custom greeting and protocols. It captures caller information, handles FAQs, and can book appointments or transfer urgent calls. All interactions are recorded and you receive instant notifications with complete call summaries.'
  }, {
    question: 'Can I customize the greeting and responses?',
    answer: 'Absolutely. You have full control over your company greeting, call handling protocols, and FAQ responses. Our system learns your business terminology and can be trained to handle industry-specific questions while maintaining your brand voice.'
  }, {
    question: "What happens if the AI can't handle a call?",
    answer: "The AI can seamlessly transfer calls to your team with full context when needed. You define the escalation rules - whether it's for urgent matters, specific request types, or VIP callers. The transfer includes all captured information so your team has complete context."
  }, {
    question: 'How quickly can I get started?',
    answer: 'Most businesses are live within 24 hours. Setup involves configuring your greeting, setting up call routing rules, and integrating with your existing tools. Our team provides hands-on support throughout the process to ensure everything works perfectly.'
  }, {
    question: 'Is my data secure and compliant?',
    answer: 'Yes. We maintain SOC 2 Type II certification and are HIPAA compliant for healthcare clients. All call data is encrypted in transit and at rest. We never share your data with third parties and provide complete audit trails for compliance requirements.'
  }, {
    question: 'What integrations are available?',
    answer: 'We integrate with major CRMs (Salesforce, HubSpot, Zoho), calendar systems (Google Calendar, Outlook, Calendly), communication tools (Slack, Teams), and can connect to custom systems via API. Our team can help set up any integration you need.'
  }];
  return <section className="w-full bg-[#0F0F0F] py-28" data-id="element-2648">
      <div className="max-w-4xl mx-auto px-8 md:px-12" data-id="element-2649">
        <div className="text-center mb-16" data-id="element-2650">
          <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#F5F5F5] mb-6" data-id="element-2651">
            Frequently Asked Questions
          </h2>
          <p className="font-['Inter'] text-lg text-[#A0A0A0] font-light" data-id="element-2652">
            Everything you need to know about our AI answering service
          </p>
        </div>

        <div className="space-y-4" data-id="element-2653">
          {faqs.map((faq, index) => <div key={index} className="bg-[#252525] border border-[#2A2A2A] rounded-2xl overflow-hidden transition-all duration-300 hover:border-[#F1B530]/30" data-id="element-2654">
              <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full px-8 py-6 flex items-center justify-between text-left" data-id="element-2655">
                <span className="font-['Inter'] font-semibold text-xl text-[#F5F5F5] pr-8" data-id="element-2656">
                  {faq.question}
                </span>
                <ChevronDownIcon className={`w-6 h-6 text-[#F1B530] flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} data-id="element-2657" />
              </button>

              <div className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-96' : 'max-h-0'}`} data-id="element-2658">
                <div className="px-8 pb-6" data-id="element-2659">
                  <p className="font-['Inter'] text-[#A0A0A0] leading-relaxed font-light" data-id="element-2660">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>)}
        </div>

        <div className="text-center mt-12" data-id="element-2661">
          <p className="font-['Inter'] text-[#A0A0A0] mb-6" data-id="element-2662">
            Still have questions? We're here to help.
          </p>
          <Button variant="secondary" theme="dark" data-id="element-2663">
            Contact Support
          </Button>
        </div>
      </div>
    </section>;
}
export function OfficeHQInspiredBase() {
  return <div className="w-full min-h-screen bg-[#0F0F0F]" data-id="element-2664">
      <ProfessionalHero data-id="element-2665" />
      <TrustBadges data-id="element-2666" />
      <HowItWorks data-id="element-2667" />
      <ServiceBreakdown data-id="element-2668" />
      <IndustrySpecificSolutions data-id="element-2669" />
      <ComparisonTable data-id="element-2670" />
      <OfficeHQPricing data-id="element-2671" />
      <OfficeHQTestimonials data-id="element-2672" />
      <OfficeHQAddOns data-id="element-2673" />
      <OfficeHQAdvancedAutomation data-id="element-2674" />
      <OfficeHQFAQ data-id="element-2675" />
    </div>;
}